// src/components/Tutorial/Examples/ReferralDashboard.js
import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  IconButton, 
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import { ContentCopy as CopyIcon, PeopleOutline as ReferralIcon, AccountBalanceWallet as WalletIcon } from '@mui/icons-material';

const ReferralDashboard = ({ authData }) => {
  const theme = useTheme();
  const origin = window.location.origin;
  const referralLink = authData ? `${origin}?ref=${authData.user_id}` : `${origin}?ref=dev_user_123`;

  return (
    <Box className="example-container" sx={{ mt: 3 }}>
      <Card sx={{ 
        borderRadius: 3, 
        boxShadow: 2, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReferralIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Referral Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invite friends and earn rewards
              </Typography>
            </Box>
          </Box>

          {/* Referral Explanation */}
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(0, 242, 254, 0.1) 100%)',
              border: '1px solid',
              borderColor: 'rgba(79, 172, 254, 0.25)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
              },
            }}
          >
            <Typography 
              variant="body1" 
              fontWeight={700} 
              sx={{ 
                mb: 1.5,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.1rem' },
              }}
            >
              Earn $1 for every friend who upgrades to VIP!
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                lineHeight: 1.8,
                color: 'text.secondary',
                mb: 1.5,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              }}
            >
              Share your referral link and get paid when they subscribe.
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                background: 'rgba(79, 172, 254, 0.15)',
                border: '1px solid',
                borderColor: 'rgba(79, 172, 254, 0.3)',
                mb: 1.5,
              }}
            >
              <Typography 
                variant="caption" 
                fontWeight={700}
                sx={{ 
                  color: 'primary.main',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                }}
              >
                Limited Time:
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                Full $1 from first month
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                lineHeight: 1.8,
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              }}
            >
              The more friends you refer, the more you earn—it's that simple.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: { xs: 2, sm: 2.5 },
              mb: 3,
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #1e88e520 0%, #1565c020 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 110,
              }}
            >
              <ReferralIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" fontWeight={700} color="primary.main">
                5
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Total Referrals
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #4caf5020 0%, #45a04920 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 110,
              }}
            >
              <WalletIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" fontWeight={700} color="success.main">
                $5
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Earnings
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #4facfe20 0%, #00f2fe20 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 110,
              }}
            >
              <ReferralIcon sx={{ fontSize: 32, color: '#4facfe', mb: 1 }} />
              <Typography variant="h5" fontWeight={700} sx={{ color: '#4facfe' }}>
                3
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Active Subs
              </Typography>
            </Box>
          </Box>

          <Stack spacing={2}>
            <TextField
              fullWidth
              value={referralLink}
              size="small"
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                color="primary"
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CopyIcon />
              </IconButton>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', flex: 1 }}>
                Click to copy your referral link
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReferralDashboard;
